import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FaTimes } from 'react-icons/fa';
import dayjs from 'dayjs';
import Modal, { Field } from '../../common/Modal';
import RolesSelection from '../../ProjectsManagement/ProjectForm/components/Employees/components/EmployeeTransfer/components/RolesSelection';
import { employeesApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { fromPickerValue } from '../../../utils/dateOnly';

const steps = ['Select Roles', 'Set Start Date'];

/**
 * Modal for adding organizational roles to an employee
 *
 * @param {Object} props
 * @param {Boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {String} props.employeeId - The employee ID
 * @param {Object} props.currentPosition - The employee's current position
 * @param {Array} props.currentRoleIds - IDs of currently assigned roles (to exclude from selection)
 * @param {Function} props.onSuccess - Callback when role addition is successful
 */
const AddRoleModal = ({
  isOpen,
  onClose,
  employeeId,
  currentPosition,
  currentRoleIds = [],
  onSuccess
}) => {
  const { showAlert } = useAlert();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startDate, setStartDate] = useState(dayjs());

  // RolesSelection state
  const [selectedPositionForRoles, setSelectedPositionForRoles] = useState(null);
  const [selectedOrganizationalRoles, setSelectedOrganizationalRoles] = useState([]);
  // Required but unused for this modal
  const [selectedFunctionalRoles, setSelectedFunctionalRoles] = useState([]);
  const [selectedTechnicalRoles, setSelectedTechnicalRoles] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setStartDate(dayjs());
      setSelectedOrganizationalRoles([]);
      setSelectedFunctionalRoles([]);
      setSelectedTechnicalRoles([]);
      // Pre-select the employee's position
      if (currentPosition?.id) {
        setSelectedPositionForRoles(currentPosition);
      }
    }
  }, [isOpen, currentPosition]);

  // Calculate if there are new roles selected
  const hasNewRoles = selectedOrganizationalRoles.some(
    roleId => !currentRoleIds.includes(roleId)
  );

  const handleSubmit = async () => {
    if (currentStep === 1) {
      // Move to step 2
      setCurrentStep(2);
      return;
    }

    // Step 2: Submit the roles
    const newRoleIds = selectedOrganizationalRoles.filter(
      roleId => !currentRoleIds.includes(roleId)
    );

    if (newRoleIds.length === 0) {
      showAlert('No new roles selected', 'warning');
      return;
    }

    try {
      setIsSubmitting(true);

      // Add each role one by one
      for (const roleId of newRoleIds) {
        await employeesApi.addRoleHistory(employeeId, {
          roleId,
          startDate: fromPickerValue(startDate) ?? undefined
        });
      }

      showAlert(
        newRoleIds.length === 1
          ? 'Role added successfully'
          : `${newRoleIds.length} roles added successfully`,
        'success'
      );
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error adding role:', error);
      showAlert(error.message || 'Error adding role', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setCurrentStep(1);
      setSelectedPositionForRoles(null);
      setSelectedOrganizationalRoles([]);
      setSelectedFunctionalRoles([]);
      setSelectedTechnicalRoles([]);
      onClose();
    }
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <RolesSelection
            selectedPositionForRoles={selectedPositionForRoles}
            setSelectedPositionForRoles={setSelectedPositionForRoles}
            selectedFunctionalRoles={selectedFunctionalRoles}
            setSelectedFunctionalRoles={setSelectedFunctionalRoles}
            selectedTechnicalRoles={selectedTechnicalRoles}
            setSelectedTechnicalRoles={setSelectedTechnicalRoles}
            selectedOrganizationalRoles={selectedOrganizationalRoles}
            setSelectedOrganizationalRoles={setSelectedOrganizationalRoles}
            initialPosition={currentPosition}
            showOrganizationalRoles={true}
            showFunctionalRoles={false}
            showTechnicalRoles={false}
            readOnly={isSubmitting}
            lockPosition={true}
          />
        );
      case 2:
        return (
          <Box sx={{ py: 2 }}>
            <Field>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => {
                  if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                    setStartDate(newValue);
                  } else if (newValue === null) {
                    setStartDate(null);
                  }
                }}
                format="DD/MM/YYYY"
                disabled={isSubmitting}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    inputProps: {
                      placeholder: 'DD/MM/YYYY'
                    }
                  }
                }}
              />
              {startDate && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    onClick={() => setStartDate(dayjs())}
                  >
                    <FaTimes size={12} /> Reset to today
                  </Typography>
                </Box>
              )}
            </Field>
          </Box>
        );
      default:
        return null;
    }
  };

  const getSubmitDisabled = () => {
    if (currentStep === 1) {
      return !hasNewRoles;
    }
    return !startDate || isSubmitting;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Organizational Roles"
      onSubmit={handleSubmit}
      submitText={
        currentStep === 2
          ? (isSubmitting ? 'Adding Roles...' : 'Add Roles')
          : 'Next'
      }
      submitDisabled={getSubmitDisabled()}
      cancelText={currentStep === 1 ? 'Cancel' : 'Back'}
      onCancel={currentStep === 1 ? handleClose : handleBack}
      width={currentStep === 1 ? '700px' : '500px'}
      submitButtonProps={{
        sx: { backgroundColor: '#2563EB', color: '#FFFFFF', '&:hover': { backgroundColor: '#1E40AF' } }
      }}
      cancelButtonProps={{
        sx: { backgroundColor: '#F1F5F9', color: '#475569', '&:hover': { backgroundColor: '#E2E8F0' } }
      }}
    >
      <Box sx={{ width: '100%', mb: 3 }}>
        <Stepper activeStep={currentStep - 1}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box>
        {getStepContent()}
      </Box>
    </Modal>
  );
};

export default AddRoleModal;
